import * as React from 'react';
import { useMemo } from 'react';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventIcon from '@mui/icons-material/Event';
import StoreIcon from '@mui/icons-material/Store';
import PeopleIcon from '@mui/icons-material/People';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ChatIcon from '@mui/icons-material/Chat';
import { useLocation } from 'react-router-dom';
import { useAbility } from '../../../permissions/AbilityContext';
import { useAuth } from '../../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useSalonAccess } from '../../../context/SalonAccessContext';
import { isProfessionalAcquisitionAllowed } from '../../../utils/nativePlatform';

export const MENU_PERMISSION_CODES = {
  appointments: 'appointments.view',
  salon: 'salon.view',
  salonTheme: 'salon.manage_theme',
  clients: 'clients.view',
  clientMessages: 'client_messages.access',
};

// Custom hook to get menu items
export const useMenuItems = () => {
  const {user} = useAuth();
  const location = useLocation();
  const ability = useAbility();
  const { t } = useTranslation();
  const { canSee } = useSalonAccess();
  const professionalAcquisitionAllowed = isProfessionalAcquisitionAllowed();

  // Check if the user is an employee or responsible
  const isEmployee = useMemo(() => {
    return user?.role === 'professional_employe';
  }, [user]);
  
  const isResponsible = useMemo(() => {
    return user?.role === 'professional_responsable';
  }, [user]);

  const isAccessManager = useMemo(() => (
    ['professional_manager', 'professional_manager_employee'].includes(user?.role)
  ), [user]);

  // Use memoization to prevent unnecessary recalculations
  const menuItems = useMemo(() => {
    const canDashboardGlobal = ability?.can('read', 'dashboard_global');
    const canDashboardSalon = ability?.can('read', 'dashboard_salon');
    const canMessaging = ability?.can('read', 'messaging');
    const showDashboard = !!(canDashboardGlobal || canDashboardSalon || isEmployee);
    const dashboardPermissionSubject = canDashboardGlobal ? 'dashboard_global' : (canDashboardSalon ? 'dashboard_salon' : 'salons');
    // Define all menu items with proper structure
    const allMenuItems = [
      // Dashboard: route differs for employee vs responsible
      ...(showDashboard
        ? (isEmployee
            ? [
                {
                  text: t('pro_menu.dashboard'),
                  icon: <DashboardIcon />,
                  path: '/pro/employe/dashboard',
                  requiredPermission: { action: 'read', subject: 'salons' },
                  showForEmployee: true,
                },
              ]
            : [
                {
                  text: t('pro_menu.dashboard'),
                  icon: <DashboardIcon />,
                  path: '/pro/dashboard',
                  requiredPermission: { action: 'read', subject: dashboardPermissionSubject },
                  showForEmployee: false,
                },
              ])
        : []),
      {
        text: t('pro_menu.appointments'),
        icon: <EventIcon />,
        path: '/pro/rendez-vous',
        requiredPermission: { action: 'read', subject: 'appointments' },
        showForEmployee: true,
        accessControlled: isAccessManager,
        visibleForAccess: !isAccessManager || canSee(MENU_PERMISSION_CODES.appointments),
      },
      // Planning as a top-level menu item only for employees
      ...(isEmployee ? [{
        text: t('pro_menu.schedule'),
        icon: <CalendarMonthIcon />,
        path: '/pro/employe/planning',
        requiredPermission: { action: 'read', subject: 'schedules' },
        showForEmployee: true
      }] : []),
      {
        text: t('pro_menu.salon'),
        icon: <StoreIcon />,
        path: '/pro/salon',
        requiredPermission: { action: 'read', subject: 'salons' },
        showForEmployee: false,
        accessControlled: isAccessManager,
        visibleForAccess: !isAccessManager
          || canSee(MENU_PERMISSION_CODES.salon)
          || canSee(MENU_PERMISSION_CODES.salonTheme),
        // children: [
        //   {
        //     text: 'Mes Salons',
        //     path: '/pro/salon',
        //     requiredPermission: { action: 'read', subject: 'salons' }
        //   },
        //   {
        //     text: 'Catégories du salon',
        //     path: '/pro/salon/category',
        //     requiredPermission: { action: 'read', subject: 'categories' }
        //   },
        //   {
        //     text: 'Service du salon',
        //     path: '/pro/salon/service',
        //     requiredPermission: { action: 'read', subject: 'services' }
        //   },
         
        // ],
      },
      {
        text: t('pro_menu.clients'),
        icon: <PeopleIcon />,
        path: '/pro/client',
        requiredPermission: { action: 'read', subject: 'users' },
        showForEmployee: false,
        accessControlled: isAccessManager,
        visibleForAccess: !isAccessManager || canSee(MENU_PERMISSION_CODES.clients),
      },
      ...(professionalAcquisitionAllowed ? [{
        text: t('pro_menu.subscription'),
        icon: <CreditCardIcon />,
        path: '/pro/subscribe',
        requiredPermission: { action: 'read', subject: 'dashboard' }, // Assuming subscription access
        showForEmployee: false,
        ownerOnly: true,
      }] : []),
      ...(canMessaging ? [{
        text: t('pro_menu.support_saloone'),
        icon: <ChatIcon />,
        path: '/pro/messaging',
        requiredPermission: { action: 'read', subject: 'messaging' },
        showForEmployee: true,
      }] : []),
      {
        text: t('pro_menu.client_messages'),
        icon: <ChatIcon />,
        path: '/pro/client-messages',
        showForEmployee: false,
        visibleForAccess: isResponsible || canSee(MENU_PERMISSION_CODES.clientMessages),
      },
      // {
      //   text: 'Employé',
      //   icon: <PeopleIcon />,
      //   path: '/pro/employe',
      //   requiredPermission: { action: 'list_employees', subject: 'users' },
      //   showForEmployee: true,
      //   children: [
      //     {
      //       text: 'Groupe',
      //       path: '/pro/employe/groupe',
      //       requiredPermission: { action: 'read', subject: 'teams' }
      //     },
      //     {
      //       text: 'Membre de groupe',
      //       path: '/pro/employe/membre-de-groupe',
      //       requiredPermission: { action: 'list_employees', subject: 'users' }
      //     },
      //     {
      //       text: 'Planning',
      //       path: '/pro/employe/planning',
      //       requiredPermission: { action: 'read', subject: 'schedules' }
      //     }
      //   ],
      // },
    ];

    // Filter items based on user role
    // For responsible users, show all items
    // For employees, only show items with showForEmployee: true
    return allMenuItems.filter((item) => {
      if (item.ownerOnly && !isResponsible) return false;
      if (item.visibleForAccess === false) return false;
      return isResponsible || !isEmployee || item.showForEmployee;
    });
  }, [isEmployee, isResponsible, isAccessManager, ability, canSee, t, professionalAcquisitionAllowed]);

  // Check if a menu item is active based on the current location
  const isActive = (path) => {
    if (path === '/pro/dashboard' && location.pathname === '/pro') {
      return true;
    }
    if (path === '/pro/employe/dashboard' && location.pathname === '/pro/employe') {
      return true;
    }
    return location.pathname.startsWith(path);
  };

  return { menuItems, isActive };
};

export default useMenuItems;
